import { ReactNode } from 'react';
import { ActivityKind } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Spinner } from '@Components/Spinner';
import { ActivityId } from '../activity-feed-data-utils';
import { soleClient } from '../activity-feed-utils';
import { useSelection } from '../useActivityFeedSelection';
import { useOracleActivity } from '../useOracleActivity';
import { useOracleCidScope } from '../useOracleCidScope';
import { ActivityFeedDetailContent } from './ActivityFeedDetailContent';
import { ActivityFeedDetailEmpty } from './ActivityFeedDetailEmpty';

export const ActivityFeedDetail = (): ReactNode => {
  const { selectedId, selectedActivity } = useSelection();
  if (!selectedActivity && selectedId) {
    return <UnloadedActivityDetail activityId={selectedId} />;
  }
  if (selectedActivity?.kind !== ActivityKind.EVENT) {
    return <ActivityFeedDetailEmpty />;
  }
  // Keyed so edit mode and unsaved edits don't carry over to the next selected event.
  return <ActivityFeedDetailContent key={selectedActivity.id} event={selectedActivity} />;
};

interface UnloadedActivityDetailProps {
  activityId: ActivityId;
}

// A linked event can sit past the loaded pages, or be a non-face member of a folded group.
const UnloadedActivityDetail = ({ activityId }: UnloadedActivityDetailProps): ReactNode => {
  const { cid, selectedCids } = useOracleCidScope();
  const activityCid = cid || soleClient(selectedCids);
  if (!activityCid) {
    return <ActivityFeedDetailEmpty />;
  }
  return <FetchedActivityDetail activityId={activityId} cid={activityCid} />;
};

interface FetchedActivityDetailProps {
  activityId: ActivityId;
  cid: string;
}

const FetchedActivityDetail = ({ activityId, cid }: FetchedActivityDetailProps): ReactNode => {
  const { activity, isLoading } = useOracleActivity(activityId, cid);
  if (isLoading) {
    return <Spinner />;
  }
  if (activity?.kind !== ActivityKind.EVENT) {
    return <ActivityFeedDetailEmpty />;
  }
  return <ActivityFeedDetailContent key={activity.id} event={activity} />;
};
